import { ContractorIdentificationType, type CreateContractorInput } from './types';

/**
 * The one canonical mapping from the contractor create form to the API input. The Contractors list's inline "Add contractor" form and the in-place
 * "Add contractor" dialog (Pay Contractors, EPN-250) both build their request here, so the rules are never copied into a lighter fork. The server stays
 * the authority on permission, tenant scoping and validation.
 */
export interface ContractorDraft {
  displayName: string;
  businessName: string;
  email: string;
  identificationType: number;
  identificationLast4: string;
  /** '' = not specified; otherwise the numeric pay method as a string (select value). */
  preferredPayMethod: string;
  /** `yyyy-MM-dd` or ''. */
  w9ReceivedOn: string;
  form1099Configured: boolean;
}

export const emptyContractorDraft: ContractorDraft = {
  displayName: '',
  businessName: '',
  email: '',
  identificationType: ContractorIdentificationType.Ssn,
  identificationLast4: '',
  preferredPayMethod: '',
  w9ReceivedOn: '',
  form1099Configured: false,
};

export interface ContractorDraftErrors {
  displayName?: string;
  email?: string;
  identificationLast4?: string;
}

export function validateContractorDraft(d: ContractorDraft): ContractorDraftErrors {
  const errors: ContractorDraftErrors = {};
  if (d.displayName.trim() === '') errors.displayName = 'Enter the contractor’s name.';
  if (d.email.trim() === '') errors.email = 'Enter an email address.';
  else if (!/^\S+@\S+\.\S+$/.test(d.email.trim())) errors.email = 'Enter a valid email address.';
  if (d.identificationLast4.trim() !== '' && !/^\d{4}$/.test(d.identificationLast4.trim())) errors.identificationLast4 = 'Enter exactly four digits.';
  return errors;
}

export function toCreateContractorInput(d: ContractorDraft): CreateContractorInput {
  return {
    displayName: d.displayName.trim(),
    businessName: d.businessName.trim() === '' ? null : d.businessName.trim(),
    email: d.email.trim(),
    identificationType: d.identificationType,
    identificationLast4: d.identificationLast4.trim() === '' ? null : d.identificationLast4.trim(),
    preferredPayMethod: d.preferredPayMethod === '' ? null : Number(d.preferredPayMethod),
    w9ReceivedOn: d.w9ReceivedOn === '' ? null : d.w9ReceivedOn,
    form1099Configured: d.form1099Configured,
  };
}
